import type { PickupPoint, ShopItem } from '../types/Checkout';

type PickupPointPaymentCheck = Pick<PickupPoint, 'delivery_provider'> | null | undefined;
type PickupPointValidationCheck =
  | Partial<Pick<PickupPoint, 'address' | 'address_full' | 'code' | 'delivery_provider'>>
  | null
  | undefined;
type ShopPickupAutoSelectCheck = Pick<ShopItem, 'code'> | null | undefined;

export type PickupPointsLoadState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export interface PickupPointsProviderResult {
  points: PickupPointValidationCheck[];
  failed: boolean;
}

export function isOnlinePaymentRequiredForPickupPoint(
  paymentOptionSlug: string | undefined,
  pickupPoint: PickupPointPaymentCheck,
) {
  return paymentOptionSlug === 'cash_upon_receipt' && !!pickupPoint && pickupPoint.delivery_provider !== 'boxberry';
}

export function getPickupPointAddress(pickupPoint: PickupPointValidationCheck): string {
  return pickupPoint?.address?.trim() || pickupPoint?.address_full?.trim() || '';
}

export function isValidPickupPoint(pickupPoint: PickupPointValidationCheck): pickupPoint is PickupPoint {
  return !!pickupPoint?.code?.trim() && !!getPickupPointAddress(pickupPoint) && !!pickupPoint.delivery_provider?.trim();
}

export function resolvePickupPointsResults(results: PickupPointsProviderResult[]): {
  points: PickupPoint[];
  state: Extract<PickupPointsLoadState, 'ready' | 'empty' | 'error'>;
} {
  const points = results.flatMap((result) => result.points).filter(isValidPickupPoint);

  if (points.length) return { points, state: 'ready' };

  return {
    points,
    state: results.some((result) => result.failed) ? 'error' : 'empty',
  };
}

export function getSingleShopToPickup<T extends ShopPickupAutoSelectCheck>(shops: T[]): NonNullable<T> | undefined {
  if (shops.length !== 1) return undefined;

  const [shop] = shops;

  return shop?.code?.trim() ? shop : undefined;
}
